/**
 * useHostLoading — 新 loading 流程（host 遮罩）的**狀態機**。協議見 `docs/LOADING_PROTOCOL.md`。
 *
 * 三個訊號、一個旗標：
 *
 *   GAME_READY { hostLoading: true }  →  這款走新流程：遮罩**別收**、等 LOAD_COMPLETE
 *   LOAD_COMPLETE                     →  資源載完 → （階段 3：補滿假進度、換開始鈕）→ 玩家按
 *   LOADING_CLOSED（host → Cocos）     →  遮罩已卸、Cocos startGame()
 *
 * 🛑 **模式判斷只看 `GAME_READY.payload.hostLoading`**，不看 bridge 版本、也不能等 LOAD_COMPLETE
 *    再判（決策點早於訊號）。舊款沒這欄 → `hostLoading=false` → 本 composable 全程不介入：
 *    遮罩照舊在 GAME_READY 收、**永遠不送 LOADING_CLOSED**（否則必達佇列的重試會打到舊款）。
 *
 * **開始鈕 / kill switch**：`deps.autoClose()`（App.vue 餵 `!LOADING_START_BUTTON`，runtime config）
 *    - `false`（預設）：LOAD_COMPLETE 後**等玩家按「開始遊戲」** → `closeLoading()`。
 *    - `true`（kill switch 關掉按鈕）：LOAD_COMPLETE 一到就自動送 LOADING_CLOSED、不顯示按鈕 ——
 *      協議〈五〉定義的退化行為，線上出事改一個 JSON 就退場。
 *
 * **假進度也在這裡**（協議 2.4；原本獨立成 useFakeProgress，同一個生命週期 → 併入）：
 *   `startProgress()`（遮罩出現時）→ **分段**爬（`PROGRESS_SEGMENTS`：login 0~20 / game 20~50 /
 *   assets 50~95），`setProgressPhase()` 換段 → LOAD_COMPLETE 內部 250ms 補滿 100
 *   → `progressDone` → `showStartButton`。
 *   刻意不接真實進度：真進度會跳、快取時瞬間 100%，體感反而差（見協議 2.4 的取捨）。
 *
 * 其他責任：
 *   - iframe reload（`handleGameReload`）→ `reset()`：Cocos 重啟會重送 GAME_READY，狀態要歸零。
 */
import { computed, ref } from 'vue'
import type { GameReadyPayload, LoadErrorPayload } from './usePostMessage'

// ── 假進度參數 ──────────────────────────────────────────────────────────────
/**
 * **分段假進度**：每個階段有自己的區間，進入該階段就跳到區間起點、再漸近爬向區間上限。
 *
 * 🛑 為什麼要分段（2026-08-20 改）：原本是**純時間驅動**的單一漸近曲線，跟階段完全脫鉤 →
 *    「切到正在加载资源」的百分比**不固定**（`GAME_READY` 0.9s 到就約 21%、3s 到就 51%、
 *    10s 到就 88%）。前面一慢，最長的「載入資源」那段就只剩幾 % 可跑 ——
 *    體感是「快滿了卻等最久」。競品是階梯式綁階段（0 → 20 → 30 → **50 開始載資源** → 緩爬），
 *    所以改成一樣：**不管前面多慢，載入資源永遠從 50% 開始**。
 */
export const PROGRESS_SEGMENTS = {
  /** 等 token */
  login: [0, 20],
  /** 等 iframe + GAME_READY */
  game: [20, 50],
  /** 等 LOAD_COMPLETE（最長的一段，留最大空間；上限刻意不到 100） */
  assets: [50, 95],
} as const satisfies Record<string, readonly [number, number]>

export type ProgressPhase = keyof typeof PROGRESS_SEGMENTS

/** 每 tick 逼近**該段上限**的比例（段內前快後慢） */
const PROGRESS_RATE = 0.025
const PROGRESS_TICK_MS = 100
/** LOAD_COMPLETE 之後從當前值補滿到 100 的時間 */
export const PROGRESS_FILL_MS = 250

/**
 * **開頭 logo splash 的最短顯示時間**（2026-08-24）。
 *
 * 為什麼要這個：新版 loading 版面（per-game 背景圖 + 進度條）的顯示條件是
 * 「有 per-game 背景圖」，而那是 host 自己第一帧就知道的 —— 結果是**只要該款有背景圖，
 * 公司 logo 一次都不會渲染**（LoadingScreen 的 logo 與新版版面是互斥的兩支）。
 *
 * 所以在最前面壓一段 splash：不管載得多快，先讓 logo 站滿 `LOGO_SPLASH_MS`，之後才換成
 * 該款的 loading 版面。
 *
 * 🛑 這**不是**憑空多出來的等待 —— 實測 `GAME_READY` 約 0.9s 到、之後還要等 `LOAD_COMPLETE`
 *    與玩家按「開始遊戲」。splash 只是把最前面那段本來就在等的時間拿來顯示 logo；
 *    只有在「登入 + GAME_READY 都比 1.5s 還快」時才會真的多等，且最多多等到 1.5s 為止。
 */
export const LOGO_SPLASH_MS = 1500

export interface HostLoadingDeps {
  /** 送 LOADING_CLOSED（走佇列、requiresAck）。由 usePostMessage 提供。 */
  sendLoadingClosed: () => void
  /**
   * kill switch：true → LOAD_COMPLETE 一到就自動關、不等按鈕（runtime `!LOADING_START_BUTTON`）。
   * 用 function 不用 boolean：config 是 `export let`（live binding），呼叫時才讀才拿得到正確值。
   * 省略 = false（等按鈕）。
   */
  autoClose?: () => boolean
  /** 卡住偵測（useLoadTimeout）。 */
  timeout: {
    arm: (stage: 'firstAlive' | 'loadAlive') => void
    disarm: () => void
    /** 強制跳出「刷新 / 等待」框（LOAD_ERROR 用） */
    show: () => void
  }
  /** 現在時刻（測試注入；預設 Date.now） */
  now?: () => number
}

export function useHostLoading(deps: HostLoadingDeps) {
  const now = deps.now ?? (() => Date.now())

  /** 這款走新流程（GAME_READY.payload.hostLoading === true）。舊款恆 false。 */
  const hostLoading = ref(false)
  /** 已收到 LOAD_COMPLETE（資源載完、可以開始） */
  const loadComplete = ref(false)
  /** 已送出 LOADING_CLOSED（遮罩已卸） */
  const loadingClosed = ref(false)
  /** 最近一次 LOAD_ERROR（null = 沒有）。抑制窗內的誤送不會寫進來。 */
  const loadError = ref<LoadErrorPayload | null>(null)
  /** 是否收過至少一則心跳（決定卡住偵測用哪個門檻，見 useLoadTimeout） */
  const aliveSeen = ref(false)

  // ── 假進度 ──
  /** 0 ~ 100 */
  const progress = ref(0)
  /** 已補滿到 100（配 maskActive / loadComplete 決定換按鈕） */
  const progressDone = ref(false)

  /**
   * 開頭 logo splash 是否已經站滿 `LOGO_SPLASH_MS`。
   * `false` 期間 App.vue 的 `showNewLoadingUi` 恆 false → 畫面停在 logo。
   */
  const splashDone = ref(false)
  let splashTimer: ReturnType<typeof setTimeout> | undefined

  /** 目前這段的上限（`PROGRESS_SEGMENTS[phase][1]`） */
  const progressCeil = ref<number>(PROGRESS_SEGMENTS.login[1])

  let progressTimer: ReturnType<typeof setInterval> | undefined

  const autoClose = deps.autoClose ?? (() => false)

  const stopProgress = (): void => {
    if (progressTimer !== undefined) {
      clearInterval(progressTimer)
      progressTimer = undefined
    }
  }

  /**
   * 換階段（App.vue watch `loadingPhase`）：跳到該段起點、之後爬向該段上限。
   * `Math.max` 保證**只進不退** —— 階段回退（理論上不該發生）不會讓進度倒著跑。
   */
  const setProgressPhase = (phase: ProgressPhase): void => {
    const [lo, hi] = PROGRESS_SEGMENTS[phase]
    progressCeil.value = hi
    if (!progressDone.value) progress.value = Math.max(progress.value, lo)
  }

  /**
   * 開始計 logo splash（App.vue 在遮罩第一次出現時呼叫）。
   * **冪等且只跑一次** —— 換 gameId / iframe reload 讓遮罩再出現時不會重播 logo
   * （那是恢復流程，玩家已經看過 logo 了，再放一次只是拖時間）。
   */
  const startSplash = (): void => {
    if (splashTimer !== undefined || splashDone.value) return
    splashTimer = setTimeout(() => {
      splashDone.value = true
    }, LOGO_SPLASH_MS)
  }

  /** 遮罩出現時呼叫（App.vue watch showLoading）：從 0 開始跑假進度。重複呼叫 = 重頭跑。 */
  const startProgress = (): void => {
    stopProgress()
    progress.value = 0
    progressDone.value = false
    progressTimer = setInterval(() => {
      const hi = progressCeil.value
      progress.value = Math.min(hi, progress.value + (hi - progress.value) * PROGRESS_RATE)
    }, PROGRESS_TICK_MS)
  }

  /** LOAD_COMPLETE → 快速補滿到 100（onLoadComplete 內部呼叫）。冪等。 */
  const completeProgress = (): void => {
    if (progressDone.value) return
    stopProgress()
    const from = progress.value
    const startAt = now()
    progressTimer = setInterval(() => {
      const t = Math.min(1, (now() - startAt) / PROGRESS_FILL_MS)
      progress.value = from + (100 - from) * t
      if (t >= 1) {
        stopProgress()
        progress.value = 100
        progressDone.value = true
      }
    }, 16)
  }

  /** host 遮罩是否還要蓋著（給 App.vue 的 showLoading 併入）。舊款恆 false（不介入）。 */
  const maskActive = computed(() => hostLoading.value && !loadingClosed.value)
  /**
   * 彈窗圖預載的閘（協議〈五〉已定案）：新款等 LOAD_COMPLETE 才載 —— GAME_READY 當下 Cocos
   * 正要載資源，那時開始抓圖等於跟遊戲本體搶頻寬。舊款無此訊號，維持 GAME_READY 就載。
   */
  const imagesAllowed = computed(() => !hostLoading.value || loadComplete.value)
  /** 進度條換「開始遊戲」鈕：新款 + 資源載完 + 進度已補滿（kill switch 下 maskActive 早已 false → 不出現） */
  const showStartButton = computed(
    () => maskActive.value && loadComplete.value && progressDone.value,
  )

  /** GAME_READY：讀旗標決定模式。🛑 必須在 markLoaded() 之前呼叫（watch 才讀得到正確模式）。 */
  function onGameReady(payload: GameReadyPayload): void {
    hostLoading.value = payload?.hostLoading === true
    loadComplete.value = false
    loadingClosed.value = false
    loadError.value = null
    aliveSeen.value = false
    if (hostLoading.value) {
      // 新款：遮罩繼續蓋著，開始量「GAME_READY → 第一則心跳」這段的停滯（15s）
      deps.timeout.arm('firstAlive')
    } else {
      // 舊款：遊戲自己接手畫面，host 不再計時
      deps.timeout.disarm()
    }
  }

  /** LOAD_ALIVE：心跳 → re-arm。Cocos 首則固定 GAME_READY + 5.0s，之後每 5s 一則。 */
  function onLoadAlive(): void {
    if (!hostLoading.value || loadComplete.value) return
    aliveSeen.value = true
    deps.timeout.arm('loadAlive')
  }

  /** LOAD_COMPLETE：資源載完。kill switch 開 → 直接關；否則等按鈕（`closeLoading()`）。 */
  function onLoadComplete(): void {
    if (!hostLoading.value) {
      // 舊款不該送這個；真收到就 warn、不做事（舊款的遮罩早在 GAME_READY 收掉了）
      console.warn('[web][Loading] 收到 LOAD_COMPLETE 但這款不是 hostLoading 模式，略過')
      return
    }
    if (loadComplete.value) return // 重複送，冪等
    loadComplete.value = true
    deps.timeout.disarm()
    completeProgress()
    if (autoClose()) closeLoading()
  }

  /**
   * 卸遮罩 + 送 LOADING_CLOSED。「開始遊戲」按鈕接這支（kill switch 開時由 onLoadComplete 自動呼叫）。
   * 冪等：只送一次（佇列自己會重試 ACK）。
   * ⚠️ LOAD_COMPLETE 之前按不會有作用（loadComplete=false 時按鈕根本不該顯示；這裡再擋一層）。
   */
  function closeLoading(): void {
    if (!hostLoading.value || !loadComplete.value || loadingClosed.value) return
    loadingClosed.value = true
    deps.sendLoadingClosed()
  }

  /**
   * LOAD_ERROR：記錄 + 跳逃生門（刷新 / 等待）。
   *
   * 歷史：這裡曾有「剛收過 AUTH_INVALID 的 5s 內忽略」抑制窗（AUTH_INVALID_GRACE_MS）——
   * 繞 Cocos「boot 中途 401 會在 AUTH_INVALID 後 ~1s 誤送 BOOTSTRAP_FAILED」的 bug。
   * Cocos 已於 2026-08-22 修復（bridge `authInvalidReported` 旗標，commit b25b28e）並實測，
   * 2026-08-24 拆除。🛑 別加回來：現在窗內吞掉的都會是**真的**錯誤。
   */
  function onLoadError(payload: LoadErrorPayload): void {
    console.error('[web][Loading] LOAD_ERROR:', payload)
    loadError.value = { code: payload?.code, message: payload?.message }
    deps.timeout.show()
  }

  /** iframe reload / 換 gameId：全部歸零（含假進度；Cocos 重啟會重送 GAME_READY）。 */
  function reset(): void {
    hostLoading.value = false
    loadComplete.value = false
    loadingClosed.value = false
    loadError.value = null
    aliveSeen.value = false
    stopProgress()
    progress.value = 0
    progressDone.value = false
    progressCeil.value = PROGRESS_SEGMENTS.login[1]
    // 🛑 `splashDone` / `splashTimer` **刻意不歸零** —— splash 是「這次開頁面」的一次性開場，
    //    iframe reload 是恢復流程，重播 logo 只會讓玩家多等。
  }

  return {
    hostLoading,
    loadComplete,
    loadingClosed,
    loadError,
    aliveSeen,
    maskActive,
    imagesAllowed,
    progress,
    progressDone,
    showStartButton,
    splashDone,
    startSplash,
    startProgress,
    setProgressPhase,
    stopProgress,
    onGameReady,
    onLoadAlive,
    onLoadComplete,
    onLoadError,
    closeLoading,
    reset,
  }
}
